const { stringify, parse } = JSON;
function deepCopy(x) {
  const stringified = stringify(x);
  return stringified ? parse(stringified) : void 0;
}
class DefaultRecordableCache {
  constructor(baseCache) {
    this.baseCache = baseCache;
    this.keysRead = /* @__PURE__ */ new Set();
    this.missingKeysRead = /* @__PURE__ */ new Set();
    this.keysUpdated = /* @__PURE__ */ new Set();
    this.metadataKeysUpdated = /* @__PURE__ */ new Set();
  }
  delete(key) {
    this.keysUpdated.add(key);
    this.baseCache.delete(key);
  }
  get(key, options) {
    this.keysRead.add(key);
    const value = this.baseCache.get(key);
    if (value === void 0) {
      this.missingKeysRead.add(key);
    }
    if (options?.copy) {
      return deepCopy(value);
    }
    return value;
  }
  set(key, value) {
    this.keysUpdated.add(key);
    this.metadataKeysUpdated.add(key);
    this.baseCache.set(key, value);
  }
  setMetadata(key, cacheControlMetadata) {
    this.metadataKeysUpdated.add(key);
    this.baseCache.setMetadata(key, cacheControlMetadata);
  }
  length() {
    return this.baseCache.length();
  }
  keys() {
    return this.baseCache.keys();
  }
  entries() {
    return this.baseCache.entries();
  }
  record() {
    return new DefaultRecordableCache(this);
  }
  filter(predicate) {
    return new DefaultFilteredCache(this, predicate);
  }
  buildFixedTimeWritableCache(generatedTime) {
    return new FixedTimeWritableCache(this, generatedTime);
  }
}
class DefaultFilteredCache {
  constructor(baseCache, predicate) {
    this.baseCache = baseCache;
    this.predicate = predicate;
  }
  delete(key) {
    this.baseCache.delete(key);
  }
  get(key, options) {
    const result = this.baseCache.get(key);
    if (result && this.predicate(key, result)) {
      if (options?.copy) {
        return deepCopy(result);
      }
      return result;
    }
    return void 0;
  }
  set(key, value) {
    this.baseCache.set(key, value);
  }
  setMetadata(key, cacheControlMetadata) {
    this.baseCache.setMetadata(key, cacheControlMetadata);
  }
  length() {
    return this.getFilteredKeys().size;
  }
  keys() {
    return this.getFilteredKeys();
  }
  entries() {
    return this.getFilteredEntries();
  }
  record() {
    return new DefaultRecordableCache(this);
  }
  filter(predicate) {
    return new DefaultFilteredCache(this, predicate);
  }
  getFilteredEntries() {
    return this.baseCache.entries().filter(([key, _value]) => {
      return this.get(key);
    });
  }
  getFilteredKeys() {
    const filteredKeySet = /* @__PURE__ */ new Set();
    this.baseCache.keys().forEach((key) => {
      if (this.get(key)) {
        filteredKeySet.add(key);
      }
    });
    return filteredKeySet;
  }
  buildFixedTimeWritableCache(generatedTime) {
    return new FixedTimeWritableCache(this, generatedTime);
  }
}
class FixedTimeWritableCache {
  constructor(baseCache, generatedTime) {
    this.baseCache = baseCache;
    this.generatedTime = generatedTime;
  }
  delete(key) {
    this.baseCache.delete(key);
  }
  get(key, options) {
    return this.baseCache.get(key, options);
  }
  set(key, value) {
    this.baseCache.set(key, {
      ...value,
      metadata: {
        ...value.metadata,
        cacheControl: { ...value.metadata.cacheControl, generatedTime: this.generatedTime }
      }
    });
  }
  setMetadata(key, cacheControlMetadata) {
    this.baseCache.setMetadata(key, {
      ...cacheControlMetadata,
      generatedTime: this.generatedTime
    });
  }
  length() {
    return this.baseCache.length();
  }
  keys() {
    return this.baseCache.keys();
  }
  entries() {
    return this.baseCache.entries();
  }
  record() {
    return new DefaultRecordableCache(this);
  }
  filter(predicate) {
    return new DefaultFilteredCache(this, predicate);
  }
  buildFixedTimeWritableCache(generatedTime) {
    return new FixedTimeWritableCache(this, generatedTime);
  }
}
class DefaultCache {
  constructor() {
    this.data = {};
  }
  get(key, options) {
    if (options?.copy) {
      return deepCopy(this.data[key]);
    }
    return this.data[key];
  }
  /**
   * Adds the specified key/value to the cache.
   *
   * @param key key at which to store value
   * @param entry value to be stored
   */
  set(key, entry) {
    if (entry.metadata.cacheControl.type === "no-store") {
      return;
    }
    this.data[key] = {
      ...entry,
      metadata: {
        ...entry.metadata,
        type: entry.metadata.type || {
          namespace: "OneStore:Internal",
          name: "UnknownType"
        },
        cacheControl: {
          generatedTime: Date.now() / 1e3,
          ...entry.metadata.cacheControl
        }
      }
    };
  }
  /**
   * Removes the cache entry associated with the specified key.
   *
   * @param key key to be removed from the store
   */
  delete(key) {
    delete this.data[key];
  }
  /**
   * Sets the metadata for the specified key if the key is in cache.
   * If the key doesn't exist, it does nothing.
   *
   * @param key key at which to store metadata
   * @param cacheControlMetadata metadata to be stored
   */
  setMetadata(key, cacheControlMetadata) {
    if (key in this.data) {
      this.data[key].metadata.cacheControl = {
        generatedTime: Date.now() / 1e3,
        ...cacheControlMetadata
      };
    }
  }
  length() {
    return this.keys().size;
  }
  keys() {
    return new Set(Object.keys(this.data));
  }
  entries() {
    return Object.entries(this.data);
  }
  record() {
    return new DefaultRecordableCache(this);
  }
  filter(predicate) {
    return new DefaultFilteredCache(this, predicate);
  }
  buildFixedTimeWritableCache(generatedTime) {
    return new FixedTimeWritableCache(this, generatedTime);
  }
}
class ClearableCache extends DefaultCache {
  clear() {
    this.data = {};
  }
}
function buildServiceDescriptor() {
  return {
    type: "cache",
    version: "1.0",
    service: new DefaultCache()
  };
}
export {
  ClearableCache,
  buildServiceDescriptor
};
//# sourceMappingURL=index.js.map
